import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import { useAuth } from '../services/authProvider';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';

// YYYY-MM-DD (local) helper
const toYMD = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatTime = (t) => (t ? String(t).substring(0, 5) : '');

const DAY_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

// การ์ดคาบสอนของครูที่ลา 1 คาบ พร้อมฟอร์มหาครูสอนแทน สำหรับมุมมองมือถือ
const PeriodCard = ({ row, c, assigned, onFind, onUpdate, onAssign, onCancel }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">คาบ {row.period_order} · ม.{row.tb_class_c_id}/{row.r_no}</div>
      <span className="fs-xs text-muted">{formatTime(row.period_begin)} - {formatTime(row.period_end)}</span>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">วิชา:</span> {row.sub_subid} {row.sub_subname}</div>
    </div>
    <div className="mt-2 pt-2 border-top">
      {assigned && !c.open ? (
        <div className="d-flex align-items-center justify-content-between gap-2">
          <span className="fs-sm"><span className="text-muted">ครูสอนแทน:</span> <Badge bg="info">{assigned.sub_teacher_name}</Badge></span>
          <Button variant="outline-danger" size="sm" onClick={onCancel}>ยกเลิก</Button>
        </div>
      ) : !c.open ? (
        <Button variant="outline-primary" size="sm" className="w-100" disabled={c.loading} onClick={onFind}>
          {c.loading ? 'กำลังค้นหา...' : 'หาครูแทน'}
        </Button>
      ) : (
        <div className="d-flex flex-column gap-2">
          <select
            className="form-select form-select-sm"
            value={String(c.selected || "")}
            onChange={e => onUpdate({ selected: e.target.value })}
          >
            <option value="">
              {c.list && c.list.length ? '-- เลือกครูที่ว่างคาบนี้ --' : 'ไม่มีครูว่างในคาบนี้'}
            </option>
            {(c.list || []).map(t => (
              <option key={t.t_id} value={String(t.t_id)}>
                {t.t_name}{t.t_phone ? ` — ☎ ${t.t_phone}` : ''}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="เหตุผล/หมายเหตุ (ไม่บังคับ)"
            value={c.reason || ""}
            onChange={e => onUpdate({ reason: e.target.value })}
          />
          <div className="d-flex gap-2">
            <Button variant="primary" size="sm" className="flex-grow-1" onClick={onAssign}>มอบหมาย</Button>
            <Button variant="light" size="sm" className="flex-grow-1" onClick={() => onUpdate({ open: false })}>ปิด</Button>
          </div>
        </div>
      )}
    </div>
  </div>
)

// การ์ดรายการสอนแทนที่มอบหมายแล้ว 1 รายการ สำหรับมุมมองมือถือ
const AssignmentCard = ({ sub, onCancel }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">คาบ {sub.period_order} · ม.{sub.tb_class_c_id}/{sub.r_no}</div>
      <Badge bg="info">{sub.sub_teacher_name}</Badge>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">วิชา:</span> {sub.sub_subid} {sub.sub_subname}</div>
      {sub.absent_teacher_name && <div><span className="text-muted">ครูที่ลา:</span> {sub.absent_teacher_name}</div>}
      {sub.reason && <div><span className="text-muted">เหตุผล:</span> {sub.reason}</div>}
    </div>
    <div className="mt-2 pt-2 border-top">
      <Button variant="outline-danger" size="sm" className="w-100" onClick={onCancel}>ยกเลิก</Button>
    </div>
  </div>
)

export default function SubstituteAssign() {
  const [skin, setSkin] = useState((localStorage.getItem('skin-mode')) ? 'dark' : '');
  const { setLoading } = useLoading();
  const { posIds } = useAuth();

  const isManager = Array.isArray(posIds) && posIds.some(p => p === 5 || p === 99);
  const myId = getUserId();

  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedDate, setSelectedDate] = useState(toYMD(new Date()));

  const [teacherList, setTeacherList] = useState([]);
  const [absentTeacherId, setAbsentTeacherId] = useState(isManager ? "" : String(myId ?? ""));

  const [periods, setPeriods] = useState([]);       // classes of the absent teacher on selectedDate
  const [candidates, setCandidates] = useState({}); // keyed by `${teach_id}-${period_id}`
  const [assignments, setAssignments] = useState([]); // existing substitutions for the absent teacher
  const [periodsView, setPeriodsView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [assignView, setAssignView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const rowKey = (teachId, periodId) => `${teachId}-${periodId}`;

  // ----- load study year -----
  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => {
        const list = res.data || [];
        let initialYearId = localStorage.getItem('yearId');
        if (!initialYearId && list.length > 0) {
          initialYearId = list[0].year_id;
          localStorage.setItem('yearId', initialYearId);
        }
        setYearId(initialYearId);
        const sel = list.find(y => String(y.year_id) === String(initialYearId));
        setSelectedYear(sel ? `ปีการศึกษา ${sel.year_year}` : "");
      })
      .catch(err => console.log("Error loading years:", err));
  }, []);

  // ----- load teacher list (managers pick anyone) -----
  useEffect(() => {
    if (!isManager) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getTeacherList?side=academic`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => setTeacherList(res.data || []))
      .catch(err => console.error("Error loading teacher list:", err));
  }, [isManager]);

  // ----- load the absent teacher's classes on the selected date -----
  const loadPeriods = useCallback(() => {
    if (!absentTeacherId || !yearId || !selectedDate) {
      setPeriods([]);
      return;
    }
    setLoading(true);
    setCandidates({});
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getSubjectTeachingOnDay/${absentTeacherId}/${yearId}?date=${selectedDate}`,
      { headers: { authorization: `Bearer ${getToken()}` } }
    )
      .then(res => setPeriods(res.data || []))
      .catch(err => {
        // 404 = ไม่มีคาบสอนในวันนั้น — ถือว่าว่างเปล่า
        if (err.response && err.response.status === 404) setPeriods([]);
        else console.error("Error loading periods:", err);
        setPeriods([]);
      })
      .finally(() => setLoading(false));
  }, [absentTeacherId, yearId, selectedDate, setLoading]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  // ----- load substitutions for the selected day -----
  // Managers (pos 5/99) see every substitution arranged that day school-wide; a subject teacher
  // sees only their own. Both feeds carry teach_id/period_id so the periods table can show the
  // substitute inline.
  const loadAssignments = useCallback(() => {
    if (!selectedDate) { setAssignments([]); return; }
    const headers = { headers: { authorization: `Bearer ${getToken()}` } };
    if (isManager) {
      axiosInstance.get(`${process.env.REACT_APP_API}/getSubstitutionsByDate/${selectedDate}`, headers)
        .then(res => setAssignments(res.data || []))
        .catch(err => {
          console.error("Error loading assignments:", err);
          setAssignments([]);
        });
    } else {
      if (!absentTeacherId) { setAssignments([]); return; }
      axiosInstance.get(
        `${process.env.REACT_APP_API}/getSubstitutionsByAbsentTeacher/${absentTeacherId}?from=${selectedDate}`,
        headers
      )
        .then(res => setAssignments((res.data || []).filter(s => toYMD(s.sub_date) === selectedDate)))
        .catch(err => {
          console.error("Error loading assignments:", err);
          setAssignments([]);
        });
    }
  }, [absentTeacherId, selectedDate, isManager]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // Lookup of the substitute already assigned to each of the shown periods (teach_id-period_id).
  const assignedByPeriod = useMemo(() => {
    const map = {};
    assignments.forEach(s => {
      if (toYMD(s.sub_date) === selectedDate) map[rowKey(s.teach_id, s.period_id)] = s;
    });
    return map;
  }, [assignments, selectedDate]);

  // ----- find free teachers for a given period -----
  const findSubstitutes = (row) => {
    const key = rowKey(row.teach_id, row.period_id);
    setCandidates(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getAvailableSubstitutes/${row.teach_id}/${row.period_id}/${selectedDate}`,
      { headers: { authorization: `Bearer ${getToken()}` } }
    )
      .then(res => {
        setCandidates(prev => ({
          ...prev,
          [key]: { list: res.data || [], selected: "", reason: "", loading: false, open: true }
        }));
      })
      .catch(err => {
        console.error("Error loading substitutes:", err);
        setCandidates(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: false } }));
        Swal.fire({ icon: 'error', title: 'ผิดพลาด', text: 'ไม่สามารถโหลดรายชื่อครูที่ว่างได้' });
      });
  };

  const updateCandidate = (key, patch) => {
    setCandidates(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  // ----- assign a substitute -----
  const assignSubstitute = (row) => {
    const key = rowKey(row.teach_id, row.period_id);
    const state = candidates[key];
    if (!state || !state.selected) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกครู', text: 'กรุณาเลือกครูสอนแทน' });
      return;
    }
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/createSubstitution`, {
      teach_id: row.teach_id,
      period_id: row.period_id,
      sub_date: selectedDate,
      sub_teacher_t_id: state.selected,
      reason: state.reason || null
    }, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(() => {
        Swal.fire({ icon: 'success', title: 'สำเร็จ', text: 'มอบหมายครูสอนแทนแล้ว', timer: 1500, showConfirmButton: false });
        updateCandidate(key, { open: false });
        loadAssignments();
      })
      .catch(err => {
        Swal.fire({
          icon: 'error',
          title: err.response?.data?.title || 'ผิดพลาด',
          text: err.response?.data?.message || 'ไม่สามารถมอบหมายได้'
        });
      })
      .finally(() => setLoading(false));
  };

  // ----- cancel a substitution -----
  const cancelAssignment = (sub) => {
    Swal.fire({
      title: 'ยกเลิกการสอนแทน?',
      text: `วิชา ${sub.sub_subid || ''} ${sub.sub_subname || ''} คาบ ${sub.period_order}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการสอนแทน',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d33'
    }).then(result => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.put(`${process.env.REACT_APP_API}/cancelSubstitution`, { sub_id: sub.sub_id },
        { headers: { authorization: `Bearer ${getToken()}` } })
        .then(() => {
          Swal.fire({ icon: 'success', title: 'ยกเลิกแล้ว', timer: 1200, showConfirmButton: false });
          loadAssignments();
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'ผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถยกเลิกได้' });
        })
        .finally(() => setLoading(false));
    });
  };

  const dayName = DAY_TH[new Date(selectedDate).getDay()];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการสอน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">การสอนแทน</li>
            </ol>
            <h4 className="main-title mb-0">มอบหมายครูสอนแทน</h4>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6">{selectedYear}</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {/* ---- filters ---- */}
                <Row className="g-3 mb-3">
                  <Col md={isManager ? 5 : 6}>
                    <label className="form-label">วันที่ลา / วันที่ต้องการสอนแทน</label>
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={e => setSelectedDate(e.target.value)}
                    />
                    <small className="text-muted">วัน{dayName}</small>
                  </Col>
                  {isManager && (
                    <Col md={5}>
                      <label className="form-label">ครูที่ลา (เจ้าของวิชา)</label>
                      <select
                        className="form-select"
                        value={String(absentTeacherId)}
                        onChange={e => setAbsentTeacherId(e.target.value)}
                      >
                        <option value="">-- เลือกครูที่ลา --</option>
                        {teacherList.map(t => (
                          <option key={t.t_id} value={String(t.t_id)}>{t.t_name}</option>
                        ))}
                      </select>
                    </Col>
                  )}
                </Row>

                {/* ---- classes on that day ---- */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <h6 className="mb-0">คาบสอนของครูในวัน{dayName}</h6>
                  <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                    <button type="button" className={`btn btn${periodsView === 'table' ? '' : '-outline'}-primary`}
                      onClick={() => setPeriodsView('table')} title="มุมมองตาราง">
                      <i className="ri-table-line" />
                    </button>
                    <button type="button" className={`btn btn${periodsView === 'card' ? '' : '-outline'}-primary`}
                      onClick={() => setPeriodsView('card')} title="มุมมองการ์ด">
                      <i className="ri-layout-grid-line" />
                    </button>
                  </div>
                </div>
                {periods.length === 0 ? (
                  <div className="text-center py-3 mb-4">
                    {absentTeacherId ? 'ไม่พบคาบสอนในวันนี้' : 'กรุณาเลือกครูที่ลา'}
                  </div>
                ) : periodsView === 'card' ? (
                  <Row className="g-3 mb-4">
                    {periods.map(row => {
                      const key = rowKey(row.teach_id, row.period_id);
                      const c = candidates[key] || {};
                      return (
                        <Col key={key} xs={12} md={6} xl={4}>
                          <PeriodCard row={row} c={c} assigned={assignedByPeriod[key]}
                            onFind={() => findSubstitutes(row)}
                            onUpdate={(patch) => updateCandidate(key, patch)}
                            onAssign={() => assignSubstitute(row)}
                            onCancel={() => cancelAssignment(assignedByPeriod[key])} />
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                <Table responsive hover className="mb-4">
                  <thead>
                    <tr className="text-center">
                      <th style={{ width: "70px" }}>คาบ</th>
                      <th style={{ width: "110px" }}>เวลา</th>
                      <th>วิชา</th>
                      <th style={{ width: "80px" }}>ชั้น</th>
                      <th style={{ width: "320px" }}>ครูสอนแทน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map(row => {
                        const key = rowKey(row.teach_id, row.period_id);
                        const c = candidates[key] || {};
                        return (
                          <tr key={key} className="text-center align-middle">
                            <th scope="row">{row.period_order}</th>
                            <td>{formatTime(row.period_begin)} - {formatTime(row.period_end)}</td>
                            <td>{row.sub_subid} {row.sub_subname}</td>
                            <td>{row.tb_class_c_id}/{row.r_no}</td>
                            <td className="text-start">
                              {assignedByPeriod[key] && !c.open ? (
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg="info">{assignedByPeriod[key].sub_teacher_name}</Badge>
                                  <Button variant="outline-danger" size="sm"
                                    onClick={() => cancelAssignment(assignedByPeriod[key])}>
                                    ยกเลิก
                                  </Button>
                                </div>
                              ) : !c.open ? (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  disabled={c.loading}
                                  onClick={() => findSubstitutes(row)}
                                >
                                  {c.loading ? 'กำลังค้นหา...' : 'หาครูแทน'}
                                </Button>
                              ) : (
                                <div className="d-flex flex-column gap-2">
                                  <select
                                    className="form-select form-select-sm"
                                    value={String(c.selected || "")}
                                    onChange={e => updateCandidate(key, { selected: e.target.value })}
                                  >
                                    <option value="">
                                      {c.list && c.list.length ? '-- เลือกครูที่ว่างคาบนี้ --' : 'ไม่มีครูว่างในคาบนี้'}
                                    </option>
                                    {(c.list || []).map(t => (
                                      <option key={t.t_id} value={String(t.t_id)}>
                                        {t.t_name}{t.t_phone ? ` — ☎ ${t.t_phone}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="เหตุผล/หมายเหตุ (ไม่บังคับ)"
                                    value={c.reason || ""}
                                    onChange={e => updateCandidate(key, { reason: e.target.value })}
                                  />
                                  <div className="d-flex gap-2">
                                    <Button variant="primary" size="sm" onClick={() => assignSubstitute(row)}>
                                      มอบหมาย
                                    </Button>
                                    <Button variant="light" size="sm" onClick={() => updateCandidate(key, { open: false })}>
                                      ปิด
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
                )}

                {/* ---- existing assignments ---- */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <h6 className="mb-0">
                    รายการสอนแทนที่มอบหมายแล้ว {isManager ? '(ทั้งโรงเรียน) ' : ''}วัน{dayName}ที่ {selectedDate}
                  </h6>
                  <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                    <button type="button" className={`btn btn${assignView === 'table' ? '' : '-outline'}-primary`}
                      onClick={() => setAssignView('table')} title="มุมมองตาราง">
                      <i className="ri-table-line" />
                    </button>
                    <button type="button" className={`btn btn${assignView === 'card' ? '' : '-outline'}-primary`}
                      onClick={() => setAssignView('card')} title="มุมมองการ์ด">
                      <i className="ri-layout-grid-line" />
                    </button>
                  </div>
                </div>
                {assignments.length === 0 ? (
                  <div className="text-center py-3">ยังไม่มีการมอบหมายสอนแทน</div>
                ) : assignView === 'card' ? (
                  <Row className="g-3">
                    {assignments.map(sub => (
                      <Col key={sub.sub_id} xs={12} md={6} xl={4}>
                        <AssignmentCard sub={sub} onCancel={() => cancelAssignment(sub)} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                <Table responsive className="mb-0">
                  <thead>
                    <tr className="text-center">
                      <th style={{ width: "70px" }}>คาบ</th>
                      <th>วิชา</th>
                      <th style={{ width: "80px" }}>ชั้น</th>
                      {isManager && <th>ครูที่ลา</th>}
                      <th>ครูสอนแทน</th>
                      <th>เหตุผล</th>
                      <th style={{ width: "90px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map(sub => (
                      <tr key={sub.sub_id} className="text-center align-middle">
                        <td>{sub.period_order}</td>
                        <td>{sub.sub_subid} {sub.sub_subname}</td>
                        <td>{sub.tb_class_c_id}/{sub.r_no}</td>
                        {isManager && <td>{sub.absent_teacher_name || '-'}</td>}
                        <td><Badge bg="info">{sub.sub_teacher_name}</Badge></td>
                        <td className="text-start">{sub.reason || '-'}</td>
                        <td>
                          <Button variant="outline-danger" size="sm" onClick={() => cancelAssignment(sub)}>
                            ยกเลิก
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
